import React, { useEffect, useRef, useState } from "react";
import { Typography, Box, Divider, Card, CardContent } from "@mui/material";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import DefaultButton from "../simple/DefaultButton";
import { ArrowForwardRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const TotalScoreCard = ({ theme, simulation, nextScenarioData }) => {
  const [showImage, setShowImage] = useState(true);
  const cardRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const cardWidth = entry.contentRect.width;
        setShowImage(cardWidth >= 350);
      }
    });

    const currentCardRef = cardRef.current;
    if (currentCardRef) {
      observer.observe(currentCardRef);
    }

    return () => {
      if (currentCardRef) {
        observer.unobserve(currentCardRef);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "fit-content",
        p: theme.customSpacing["2xl"],
        border: `2px solid ${theme.palette.border.tertiary}`,
        borderRadius: "8px",
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          pb: theme.customSpacing["3xl"],
        }}
      >
        <Typography
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.textLG.fontSize,
          }}
        >
          {simulation.evaluation.total_score?.title || "Total Score"}
        </Typography>
        <Typography
          sx={{
            fontStyle: "italic",
            fontWeight: theme.typography.fontWeightLight,
            fontSize: theme.typography.textSM.fontSize,
          }}
        >
          Simulation Challenge: {simulation.scenario.name || "Scenario"}
        </Typography>
      </Box>
      <Divider
        sx={{
          backgroundColor: theme.palette.border.tertiary,
          height: 2,
          alignSelf: "stretch",
          mx: `-${theme.customSpacing["2xl"]}`,
        }}
      />
      <Box
        sx={{
          height: "200px",
          width: "200px",
          margin: theme.customSpacing["3xl"],
        }}
      >
        <CircularProgressbar
          value={
            simulation.evaluation.total_score?.value ||
            simulation.evaluation.total_score
          }
          text={`${
            (simulation.evaluation.total_score?.value / 10.0).toFixed(1) ||
            simulation.evaluation.total_score
          }`}
          strokeWidth={12.5}
          styles={buildStyles({
            pathColor: theme.palette.border.brand,
            trailColor: theme.palette.border.secondary,
            textColor: theme.palette.text.primary,
            pathTransition: "stroke-dashoffset 0.5s ease 0s",
            strokeLinecap: "round",
          })}
        />
      </Box>

      <Box
        sx={{
          mb: theme.customSpacing["3xl"],
        }}
      >
        <Typography
          sx={{
            fontSize: theme.typography.textLG.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          Nice work {simulation?.user?.first_name || null} 👏
        </Typography>
        <Typography
          sx={{
            fontSize: theme.typography.textSM.fontSize,
            fontWeight: theme.typography.fontWeightRegular,
          }}
        >
          You’re on a roll, keep it up!
        </Typography>
      </Box>
      {nextScenarioData && (
        <Card
          ref={cardRef}
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            flexGrow: 1,
            color: theme.palette.text.secondary,
            backgroundColor: "transparent",
            boxShadow:
              "0px 12px 16px -4px rgba(0, 91, 140, 0.10), 0px 4px 6px -2px rgba(0, 91, 140, 0.03), 0px 2px 2px -1px rgba(0, 91, 140, 0.04)",
            border: `2px solid ${theme.palette.border.tertiary}`,
            borderRadius: theme.shape.radius,
          }}
        >
          {showImage && (
            <Box
              sx={{
                height: "auto",
                width: "40%",
                backgroundColor: "white",
                backgroundImage: nextScenarioData.image
                  ? `url(${nextScenarioData.image})`
                  : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <CardContent
            sx={{
              flexGrow: 1,
              backgroundColor: "transparent",
              justifyContent: "space-evenly",
              color: theme.palette.text.primary,
              padding: theme.containers.paddingMobile,
            }}
          >
            <Typography
              gutterBottom
              sx={{
                mb: theme.customSpacing.lg,
                fontSize: theme.typography.textXS.fontSize,
                fontWeight: theme.typography.fontWeightLight,
                color: theme.palette.text.gray,
              }}
            >
              Up Next!
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TotalScoreCard;
